import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import './CampReportModal.css';

interface Camp {
  id: string;
  name: string;
}

interface CampDay {
  id: string;
  date: string;
  day_number: number;
  week_number: number;
  is_available: boolean;
}

interface CampEnrollmentReport {
  id: string;
  student_name: string;
  parent_name: string;
  parent_email: string;
  parent_phone: string | null;
  enrollment_type: string;
  formatted_days: string;
  needs_early_dropoff: boolean;
  needs_late_pickup: boolean;
  early_dropoff_fee?: number;
  late_pickup_fee?: number;
  early_dropoff_days_formatted?: string;
  late_pickup_days_formatted?: string;
  camp_name?: string;
  days_attending?: string[];
  selected_weeks?: number[];
  camp_id?: string;
}

interface CampReportModalProps {
  onClose: () => void;
}

const CampReportModal: React.FC<CampReportModalProps> = ({ onClose }) => {
  const [camps, setCamps] = useState<Camp[]>([]);
  const [selectedCampId, setSelectedCampId] = useState<string>('');
  const [enrollments, setEnrollments] = useState<CampEnrollmentReport[]>([]);
  const [campDays, setCampDays] = useState<CampDay[]>([]);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [loadingCamps, setLoadingCamps] = useState(true);

  useEffect(() => {
    fetchCamps();
  }, []);

  const fetchCamps = async () => {
    try {
      setLoadingCamps(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCamps(data);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
    } finally {
      setLoadingCamps(false);
    }
  };

  const fetchCampDays = async (campId: string) => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}/days`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Sort by date and filter available days
        const sortedDays = data
          .filter((day: CampDay) => day.is_available)
          .sort((a: CampDay, b: CampDay) => new Date(a.date).getTime() - new Date(b.date).getTime());
        console.log('Camp days fetched:', sortedDays.length, sortedDays);
        setCampDays(sortedDays);
      } else {
        console.error('Failed to fetch camp days:', response.status);
        setCampDays([]);
      }
    } catch (error) {
      console.error('Error fetching camp days:', error);
      setCampDays([]);
    }
  };

  const fetchCampReport = async (campId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}/enrollments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Get camp name
        const selectedCamp = camps.find(c => c.id === campId);
        const enrollmentsWithCampName = data.map((enrollment: any) => ({
          ...enrollment,
          camp_name: selectedCamp?.name || 'Unknown Camp'
        }));
        setEnrollments(enrollmentsWithCampName);
      } else {
        console.error('Failed to fetch camp enrollments');
        setEnrollments([]);
      }
    } catch (error) {
      console.error('Error fetching camp report:', error);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCampDays = async () => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const allDays: CampDay[] = [];
      
      // Fetch days for each camp
      for (const camp of camps) {
        try {
          const response = await fetch(`${config.BACKEND_URL}/api/camps/${camp.id}/days`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const availableDays = data.filter((day: CampDay) => day.is_available);
            allDays.push(...availableDays);
          }
        } catch (error) {
          console.error(`Error fetching days for camp ${camp.id}:`, error);
        }
      }
      
      // Remove duplicates and sort by date
      const uniqueDays = Array.from(
        new Map(allDays.map(day => [day.date, day])).values()
      ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      console.log('All camp days fetched:', uniqueDays.length, uniqueDays);
      setCampDays(uniqueDays);
    } catch (error) {
      console.error('Error fetching all camp days:', error);
      setCampDays([]);
    }
  };

  const fetchAllCampReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/all-camps/enrollments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('All camp enrollments data:', data);
        console.log('Number of enrollments:', data?.length || 0);
        setEnrollments(data || []);
      } else {
        console.error('Failed to fetch all camp enrollments', response.status, response.statusText);
        setEnrollments([]);
      }
    } catch (error) {
      console.error('Error fetching all camp reports:', error);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCampChange = async (campId: string) => {
    setSelectedCampId(campId);
    setActiveTab('all'); // Reset to all tab when camp changes
    if (campId === 'all') {
      await fetchAllCampReports();
      await fetchAllCampDays();
    } else if (campId) {
      await fetchCampReport(campId);
      await fetchCampDays(campId);
    } else {
      setEnrollments([]);
      setCampDays([]);
    }
  };

  const formatDateForTab = (dateString: string) => {
    // Parse date string manually to avoid timezone issues
    // Extract date part (YYYY-MM-DD) if it includes time
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    // Create date in local timezone (no UTC conversion)
    const localDate = new Date(year, month - 1, day);
    const monthName = localDate.toLocaleDateString('en-US', { month: 'short' });
    return `${monthName} ${day}`;
  };

  const isEnrollmentForDay = (enrollment: CampEnrollmentReport, dayDate: string, dayWeekNumber?: number): boolean => {
    // Normalize date format (ensure YYYY-MM-DD)
    const normalizedDayDate = dayDate.split('T')[0]; // Remove time if present
    
    // Check if enrollment is for this specific day
    if (enrollment.enrollment_type === 'drop_in') {
      // Drop-in enrollment - check if day is in days_attending
      if (enrollment.days_attending && enrollment.days_attending.length > 0) {
        return enrollment.days_attending.some((d: string) => d.split('T')[0] === normalizedDayDate);
      }
      return false;
    } else if (enrollment.enrollment_type === 'weekly') {
      // Weekly enrollment - check if day's week is in selected_weeks
      if (enrollment.selected_weeks && enrollment.selected_weeks.length > 0 && dayWeekNumber !== undefined) {
        return enrollment.selected_weeks.includes(dayWeekNumber);
      }
      return false;
    } else if (enrollment.enrollment_type === 'full_camp') {
      // Full camp enrollment - includes all days
      return true;
    }
    
    return false;
  };

  const getFilteredEnrollments = (): CampEnrollmentReport[] => {
    if (activeTab === 'all') {
      return enrollments;
    }
    
    // Filter enrollments for the selected day
    const day = campDays.find(d => d.id === activeTab);
    if (!day) return [];
    
    return enrollments.filter(enrollment => {
      // For both "all camps" and specific camp, filter by day
      return isEnrollmentForDay(enrollment, day.date, day.week_number);
    });
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return 'N/A';
    // Format phone number if it's 10 digits
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="camp-report-modal-overlay" onClick={onClose}>
      <div className="camp-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="camp-report-header">
          <h2>📊 Camp Enrollment Report</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <div className="camp-report-content">
          <div className="camp-selector">
            <label htmlFor="camp-select">Select Camp:</label>
            <select
              id="camp-select"
              value={selectedCampId}
              onChange={(e) => handleCampChange(e.target.value)}
              disabled={loadingCamps}
            >
              <option value="">-- Select a camp --</option>
              <option value="all">All Camps</option>
              {camps.map(camp => (
                <option key={camp.id} value={camp.id}>{camp.name}</option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="loading-message">
              <div className="spinner"></div>
              <p>Loading enrollment data...</p>
            </div>
          )}

          {!loading && selectedCampId && (
            <>
              {/* Tabs - show for both specific camps and "all camps" */}
              <div className="camp-report-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e9ecef', overflowX: 'auto', paddingBottom: '0' }}>
                <button
                  className={`camp-report-tab ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                  style={{ padding: '10px 16px', border: 'none', background: activeTab === 'all' ? '#e7f3ff' : 'transparent', color: activeTab === 'all' ? '#007bff' : '#6c757d', fontSize: '14px', fontWeight: activeTab === 'all' ? 600 : 500, cursor: 'pointer', borderBottom: activeTab === 'all' ? '3px solid #007bff' : '3px solid transparent', whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                >
                  All
                </button>
                {campDays.length > 0 ? (
                  campDays.map((day) => (
                    <button
                      key={day.id}
                      className={`camp-report-tab ${activeTab === day.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(day.id)}
                      style={{ padding: '10px 16px', border: 'none', background: activeTab === day.id ? '#e7f3ff' : 'transparent', color: activeTab === day.id ? '#007bff' : '#6c757d', fontSize: '14px', fontWeight: activeTab === day.id ? 600 : 500, cursor: 'pointer', borderBottom: activeTab === day.id ? '3px solid #007bff' : '3px solid transparent', whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                    >
                      {formatDateForTab(day.date)}
                    </button>
                  ))
                ) : (
                  <div style={{ padding: '10px', color: '#6c757d', fontStyle: 'italic' }}>
                    Loading camp days... (Selected: {selectedCampId})
                  </div>
                )}
              </div>

              {getFilteredEnrollments().length === 0 && (
                <div className="empty-message">
                  <p>
                    {activeTab === 'all' 
                      ? `No enrollments found${selectedCampId === 'all' ? ' for any camp' : ' for this camp'}.`
                      : `No enrollments found for ${campDays.find(d => d.id === activeTab) ? formatDateForTab(campDays.find(d => d.id === activeTab)!.date) : 'this day'}.`
                    }
                  </p>
                </div>
              )}

              {getFilteredEnrollments().length > 0 && (
                <div className="camp-report-table-container">
                  <table className="camp-report-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Parent Name</th>
                        <th>Parent Email</th>
                        <th>Parent Phone</th>
                        <th>Days Enrolled</th>
                        <th>Early Drop-off</th>
                        <th>Late Pick-up</th>
                        <th>Camp Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredEnrollments().map((enrollment) => (
                        <tr key={enrollment.id}>
                          <td>{enrollment.student_name}</td>
                          <td>{enrollment.parent_name || 'N/A'}</td>
                          <td>{enrollment.parent_email || 'N/A'}</td>
                          <td>{formatPhone(enrollment.parent_phone)}</td>
                          <td>{enrollment.formatted_days || 'N/A'}</td>
                          <td>
                            {enrollment.needs_early_dropoff ? (
                              enrollment.early_dropoff_days_formatted || 'Yes'
                            ) : (
                              'No'
                            )}
                          </td>
                          <td>
                            {enrollment.needs_late_pickup ? (
                              enrollment.late_pickup_days_formatted || 'Yes'
                            ) : (
                              'No'
                            )}
                          </td>
                          <td>{enrollment.camp_name || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="camp-report-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampReportModal;


